import React, { useCallback, useMemo, useState } from 'react';

import { Navigate, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag, EPopupSubType, OfferType } from 'constants/enums';
import { CircleFadingPlus } from 'lucide-react';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';

import { OfferPageLayout } from '../../components/OfferPageLayout';
import { useOfferTable } from '../../hooks/useOfferTable';
import { OffersFilterId } from '../../OfferFilters.types';

import { CheatsDialog } from './components/CheatsDialog';
import { useRewardCalendarColumns } from './hooks/useRewardCalendarColumns';

const RewardCalendarTable = (): React.ReactElement | null => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_REWARD_CALENDAR]) {
    return <Navigate to="../popups" replace />;
  }

  return <RewardCalendarTableContent />;
};

const RewardCalendarTableContent = (): React.ReactElement => {
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const hasFeatureFlagCheats = !!featureFlags?.[EFeatureFlag.DASHBOARD_ROLLING_CHEATS];

  const [cheatsOfferId, setCheatsOfferId] = useState<string | null>(null);
  const onOpenCheats = useCallback((offerId: string) => {
    setCheatsOfferId(offerId);
  }, []);
  const handleCloseCheats = useCallback(() => {
    setCheatsOfferId(null);
  }, []);

  const {
    getOffers,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    sorting,
    updateSorting,
    hasActiveFilters,
    permissions,
    canManageOffers,
    canUserEdit,
    actions,
    selectedOffer,
    isDeleteDialogOpen,
    showLimitedEditModal,
    handleDeleteOffer,
    handleCreate,
    handleCloseLimitedEditModal,
    handleCloseDeleteDialog,
    offers,
    processOffers,
    updateRewardCalendarCollectableDay,
    updateRewardCalendarUnlockedIndex
  } = useOfferTable({
    offerType: OfferType.POPUP,
    offerSubType: EPopupSubType.REWARD_CALENDAR,
    offerLabel: 'Reward Calendar',
    needsPricings: false
  });

  const handleCheatsSubmit = useCallback<
    React.ComponentProps<typeof CheatsDialog>['onSubmit']
  >(
    ({ indexCollected, playerId, offerId }, options) => {
      updateRewardCalendarCollectableDay.mutate(
        { targetIndex: indexCollected, playerId, offerId },
        options
      );
    },
    [updateRewardCalendarCollectableDay]
  );

  const handleCheatsUnlockIndexSubmit = useCallback<
    React.ComponentProps<typeof CheatsDialog>['onSubmitUnlockIndex']
  >(
    ({ targetIndex, playerId, offerId }, options) => {
      updateRewardCalendarUnlockedIndex.mutate(
        { targetIndex, playerId, offerId },
        options
      );
    },
    [updateRewardCalendarUnlockedIndex]
  );

  const { columns } = useRewardCalendarColumns({
    sorting,
    updateSorting,
    isLoading: getOffers.isLoading,
    actions,
    permissions,
    hasFeatureFlagCheats,
    onOpenCheats
  });

  const processed = useMemo(
    () => processOffers(offers),
    [offers, processOffers]
  );

  return (
    <OfferPageLayout
      title="Reward Calendars"
      buttonText={processed.buttonText}
      onCreateClick={handleCreate}
      canUserEdit={canUserEdit}
      canManageOffers={canManageOffers}
      offerLabel="Reward Calendars"
      showFilters={offers.length > 0}
      hasActiveFilters={hasActiveFilters}
      filterBarConfig={{
        hiddenFilters: [OffersFilterId.PRICE, OffersFilterId.SCHEDULE]
      }}
      offers={offers}
      displayData={processed.sorted}
      columns={columns}
      totalCount={processed.totalCount}
      totalPages={processed.totalPages}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      isLoading={getOffers.isLoading}
      emptyStateTitle="Add your first Reward Calendar"
      emptyStateDescription="Encourage daily engagement by offering players rewards they can collect each day. Set up your calendar to define rewards, progression, and behavior."
      createButtonPrefix="Reward Calendar"
      emptyStateIcon={
        <CircleFadingPlus className="text-base-mutedForeground h-6 w-6" />
      }
      bodyDataTestId="dashRewardCalendarList"
      offerType={OfferType.POPUP}
      offerDesignType={OfferDesignTypes.PopUp}
      selectedOffer={selectedOffer}
      showLimitedEditModal={showLimitedEditModal}
      onCloseLimitedEditModal={handleCloseLimitedEditModal}
      isDeleteDialogOpen={isDeleteDialogOpen}
      onCloseDeleteDialog={handleCloseDeleteDialog}
      onConfirmDelete={handleDeleteOffer}
      deleteModalTitle="Delete Reward Calendar"
      deleteModalDescription="Are you sure you want to delete this reward calendar?"
    >
      <CheatsDialog
        cheatsOfferId={cheatsOfferId}
        onSubmit={handleCheatsSubmit}
        onSubmitUnlockIndex={handleCheatsUnlockIndexSubmit}
        onClose={handleCloseCheats}
      />
    </OfferPageLayout>
  );
};

export { RewardCalendarTable };
